// Comprehensive data format validation for Laravel backend
export const validateAttendeeDataFormat = (formData, uniqueFields, isUpdate = false) => {
  if (import.meta.env.MODE === 'development') {
    console.log('🔍 Validating registration data...');
  }
  
  // Find standard fields
  const fullNameField = uniqueFields.find(f => f.field_name === 'full_name');
  const emailField = uniqueFields.find(f => f.field_name === 'email');
  const phoneField = uniqueFields.find(f => f.field_name === 'phone');
  const organizationField = uniqueFields.find(f => f.field_name === 'organization');
  const positionField = uniqueFields.find(f => f.field_name === 'position');
  
  if (import.meta.env.MODE === 'development') {
    console.log('🔍 Standard fields found:', {
      fullName: !!fullNameField,
      email: !!emailField,
      phone: !!phoneField,
      organization: !!organizationField,
      position: !!positionField
    });
  }
  
  // Build custom_fields object excluding standard fields
  const standardFieldIds = [
    fullNameField?.id,
    emailField?.id,
    phoneField?.id,
    organizationField?.id,
    positionField?.id
  ].filter(id => id !== undefined);
  
  // Filter out standard fields from custom_fields
  const customFields = {};
  Object.keys(formData).forEach(fieldId => {
    if (!standardFieldIds.includes(fieldId) && formData[fieldId] !== '') {
      // Verify this is actually a custom field by checking if it exists in uniqueFields
      const field = uniqueFields.find(f => f.id === fieldId);
      if (field && !['full_name', 'email', 'phone', 'organization', 'position'].includes(field.field_name)) {
        customFields[fieldId] = formData[fieldId];
        if (import.meta.env.MODE === 'development') {
          console.log(`✅ Custom field: ${field.field_name} = "${formData[fieldId]}"`);
        }
      }
    }
  });
  
  // Build the exact format Laravel expects - send all field data directly by field name
  const attendeeData = {};
  
  // Add all active field data by field name
  uniqueFields.forEach(field => {
    if (field.is_active) {
      // For updates, include all active fields (even if empty) to allow clearing fields
      // For new registrations, only include fields with values
      const hasValue = formData[field.id] !== undefined && formData[field.id] !== '';
      const shouldInclude = isUpdate || hasValue;
      
      if (shouldInclude) {
        attendeeData[field.field_name] = formData[field.id] || '';
        if (import.meta.env.MODE === 'development') {
          console.log(`✅ Field data: ${field.field_name} = "${formData[field.id] || ''}" (update: ${isUpdate})`);
        }
      } else if (import.meta.env.MODE === 'development') {
        console.log(`⏭️ Skipped field: ${field.field_name}, active: ${field.is_active}, hasValue: ${hasValue}, isUpdate: ${isUpdate}`);
      }
    }
  });
  
  // NOTE: We no longer send custom_fields object with UUIDs
  // Laravel backend now expects only field names (full_name, email, phone_number, etc.)
  // The backend will save all non-standard fields to attendee_data table automatically
  
  if (import.meta.env.MODE === 'development') {
    console.log('✅ Registration data formatted:', {
      totalFields: Object.keys(attendeeData).length,
      fieldNames: Object.keys(attendeeData),
      data: attendeeData
    });
  }
  
  // Validate required fields dynamically based on active fields
  const validationErrors = [];
  
  // Check each field for required validation
  uniqueFields.forEach(field => {
    // Only active fields can be required
    let isFieldRequired = false;
    if (field.is_active) {
      isFieldRequired = field.is_required || field.is_mandatory_by_event;
    }
    
    if (import.meta.env.MODE === 'development') {
      console.log(`🔍 Field validation: ${field.field_name}, active: ${field.is_active}, required: ${isFieldRequired}, value: "${attendeeData[field.field_name] || ''}"`);
    }
    
    if (isFieldRequired) {
      const fieldValue = attendeeData[field.field_name];
      if (!fieldValue || fieldValue.trim() === '') {
        validationErrors.push(`${field.field_name} is required`);
        if (import.meta.env.MODE === 'development') {
          console.error(`❌ Required field missing: ${field.field_name}`);
        }
      }
    }
  });
  
  if (validationErrors.length > 0 && import.meta.env.MODE === 'development') {
    console.error('❌ Validation errors:', validationErrors);
  }
  
  return {
    isValid: validationErrors.length === 0,
    errors: validationErrors,
    data: attendeeData
  };
};

// Test with sample data
export const testSampleData = () => {
  const sampleFormData = {
    '1': 'John Doe',
    '2': 'john@example.com',
    '3': '+1234567890',
    '4': 'Tech Corp',
    '5': 'Developer'
  };
  
  const sampleFields = [
    { id: '1', field_name: 'full_name', field_label: 'Full Name' },
    { id: '2', field_name: 'email', field_label: 'Email' },
    { id: '3', field_name: 'phone', field_label: 'Phone' },
    { id: '4', field_name: 'organization', field_label: 'Organization' },
    { id: '5', field_name: 'position', field_label: 'Position' }
  ];
  
  return validateAttendeeDataFormat(sampleFormData, sampleFields);
};
